import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { analyticsApi } from '../api/analytics.api';
import type { GrowthAnalyticsParams } from '../api/analytics.api';
import { analyticsKeys } from '../api/analytics.keys';

export function useAnalyticsEvents(params: any) {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: [...analyticsKeys.eventList(params), i18n.language],
    queryFn: () => analyticsApi.listEvents(params),
    placeholderData: keepPreviousData,
  });
}

export function useAnalyticsEventsSummary(params: any) {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: [...analyticsKeys.eventSummary(params), i18n.language],
    queryFn: () => analyticsApi.eventsSummary(params),
    placeholderData: keepPreviousData,
  });
}

export function useGrowthAnalytics(section: 'overview' | 'funnels' | 'search' | 'marketplace' | 'attribution' | 'retention', params: GrowthAnalyticsParams) {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: [...analyticsKeys.growth(section, params), i18n.language],
    queryFn: () => {
      switch (section) {
        case 'overview': return analyticsApi.growthOverview(params);
        case 'funnels': return analyticsApi.growthFunnels(params);
        case 'search': return analyticsApi.growthSearch(params);
        case 'marketplace': return analyticsApi.growthMarketplace(params);
        case 'attribution': return analyticsApi.growthAttribution(params);
        case 'retention': return analyticsApi.growthRetention(params);
      }
    },
    placeholderData: keepPreviousData,
  });
}

export function useDemandIntelligence(params: GrowthAnalyticsParams) {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: [...analyticsKeys.growth('demand-intelligence', params), i18n.language],
    queryFn: () => analyticsApi.demandIntelligence(params),
    placeholderData: keepPreviousData,
  });
}

export function useAnalyticsGeoCities() {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: [...analyticsKeys.geoCities(), i18n.language],
    queryFn: () => analyticsApi.geoCities(),
    placeholderData: keepPreviousData,
  });
}

export function useAnalyticsGeoDistricts(cityId?: number | string | null) {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: [...analyticsKeys.geoDistricts(cityId), i18n.language],
    queryFn: () => analyticsApi.geoDistricts(cityId),
    enabled: cityId !== undefined && cityId !== null && cityId !== '',
    placeholderData: keepPreviousData,
  });
}
